/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  Input,
  Inject,
  NgZone,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  InjectionToken,
  Optional,
} from '@angular/core';
import {RippleConfig, RippleRenderer} from './ripple-renderer';
import {RippleRef} from './ripple-ref';
import {ViewportRuler} from '../overlay/position/viewport-ruler';
import {Platform} from '../platform/platform';

export interface RippleGlobalOptions {
  disabled?: boolean;
  baseSpeedFactor?: number;
}
/**
 * Injection token that can be used to specify the global ripple options.
 */
export const MD_RIPPLE_GLOBAL_OPTIONS =
    new InjectionToken<RippleGlobalOptions>('md-ripple-global-options');
export class MdRipple implements OnChanges, OnDestroy {

  /**
   * The element that triggers the ripple when click events are received. Defaults to the
   * directive's host element.
   */
  // Prevent TS metadata emit from referencing HTMLElement in ripple.js
  // Otherwise running this code in a Node environment (e.g Universal) will not work.
   trigger: HTMLElement|HTMLElement;
/**
 * Whether the ripple always originates from the center of the host element's bounds, rather
 * than originating from the location of the click event.
 */
centered: boolean;
/**
 * Whether click events will not trigger the ripple. It can still be triggered by manually
 * calling createRipple()
 */
disabled: boolean;
/**
 * If set, the radius in pixels of foreground ripples when fully expanded. If unset, the radius
 * will be the distance from the center of the ripple to the furthest corner of the host element's
 * bounding rectangle.
 */
radius: number = 0;
/**
 * If set, the normal duration of ripple animations is divided by this value. For example,
 * setting it to 0.5 will cause the animations to take twice as long.
 * A changed speedFactor will not modify the fade-out duration of the ripples.
 */
speedFactor: number = 1;
/**
 * Custom color for ripples.
 */
color: string;
/**
 * Whether foreground ripples should be visible outside the component's bounds.
 */
unbounded: boolean;
/**
 * Renderer for the ripple DOM manipulations.
 */
private _rippleRenderer: RippleRenderer;
/**
 * Options that are set globally for all ripples.
 */
private _globalOptions: RippleGlobalOptions;
/**
 * @param {?} elementRef
 * @param {?} ngZone
 * @param {?} ruler
 * @param {?} platform
 * @param {?} globalOptions
 */
constructor(
    elementRef: ElementRef,
    ngZone: NgZone,
    ruler: ViewportRuler,
    platform: Platform,
      globalOptions: RippleGlobalOptions
  ) {
    this._rippleRenderer = new RippleRenderer(elementRef, ngZone, ruler, platform);
    this._globalOptions = globalOptions ? globalOptions : {};

    this._updateRippleRenderer();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (changes['trigger'] && this.trigger) {
      this._rippleRenderer.setTriggerElement(this.trigger);
    }

    this._updateRippleRenderer();
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    // Set the trigger element to null to cleanup all listeners.
    this._rippleRenderer.setTriggerElement(null);
  }
/**
 * Launches a manual ripple at the specified position.
 * @param {?} pageX
 * @param {?} pageY
 * @param {?=} config
 * @return {?}
 */
launch(pageX: number, pageY: number, config = this.rippleConfig): RippleRef {
    return this._rippleRenderer.fadeInRipple(pageX, pageY, config);
  }
/**
 * Fades out all currently showing ripple elements.
 * @return {?}
 */
fadeOutAll() {
    this._rippleRenderer.fadeOutAll();
  }
/**
 * Ripple configuration from the directive's input values.
 * @return {?}
 */
get rippleConfig(): RippleConfig {
    return {
      centered: this.centered,
      speedFactor: this.speedFactor * (this._globalOptions.baseSpeedFactor || 1),
      radius: this.radius,
      color: this.color
    };
  }
/**
 * Updates the ripple renderer with the latest ripple configuration.
 * @return {?}
 */
private _updateRippleRenderer() {
    this._rippleRenderer.rippleDisabled = this._globalOptions.disabled || this.disabled;
    this._rippleRenderer.rippleConfig = this.rippleConfig;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[md-ripple], [mat-ripple], [mdRipple], [matRipple]',
  exportAs: 'mdRipple',
  host: {
    'class': 'mat-ripple',
    '[class.mat-ripple-unbounded]': 'unbounded'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: ViewportRuler, },
{type: Platform, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_RIPPLE_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'trigger': [{ type: Input, args: ['mdRippleTrigger', ] },],
'centered': [{ type: Input, args: ['mdRippleCentered', ] },],
'disabled': [{ type: Input, args: ['mdRippleDisabled', ] },],
'radius': [{ type: Input, args: ['mdRippleRadius', ] },],
'speedFactor': [{ type: Input, args: ['mdRippleSpeedFactor', ] },],
'color': [{ type: Input, args: ['mdRippleColor', ] },],
'unbounded': [{ type: Input, args: ['mdRippleUnbounded', ] },],
};
}

function MdRipple_tsickle_Closure_declarations() {
/** @type {?} */
MdRipple.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdRipple.ctorParameters;
/** @type {?} */
MdRipple.propDecorators;
/** @type {?} */
MdRipple.prototype.trigger;
/**
 * Whether the ripple always originates from the center of the host element's bounds, rather
 * than originating from the location of the click event.
 * @type {?}
 */
MdRipple.prototype.centered;
/**
 * Whether click events will not trigger the ripple. It can still be triggered by manually
 * calling createRipple()
 * @type {?}
 */
MdRipple.prototype.disabled;
/**
 * If set, the radius in pixels of foreground ripples when fully expanded. If unset, the radius
 * will be the distance from the center of the ripple to the furthest corner of the host element's
 * bounding rectangle.
 * @type {?}
 */
MdRipple.prototype.radius;
/**
 * If set, the normal duration of ripple animations is divided by this value. For example,
 * setting it to 0.5 will cause the animations to take twice as long.
 * A changed speedFactor will not modify the fade-out duration of the ripples.
 * @type {?}
 */
MdRipple.prototype.speedFactor;
/**
 * Custom color for ripples.
 * @type {?}
 */
MdRipple.prototype.color;
/**
 * Whether foreground ripples should be visible outside the component's bounds.
 * @type {?}
 */
MdRipple.prototype.unbounded;
/**
 * Renderer for the ripple DOM manipulations.
 * @type {?}
 */
MdRipple.prototype._rippleRenderer;
/**
 * Options that are set globally for all ripples.
 * @type {?}
 */
MdRipple.prototype._globalOptions;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
